import { Divider, Grid, Typography } from '@mui/material'
import { Box } from '@mui/system'
import { useEffect, useMemo, useState } from 'react'
import { Form } from 'react-final-form'
import { useNavigate, useParams } from 'react-router-dom'
import { toast } from 'react-toastify'
import { DiscardButton, SaveButton } from 'src/components/button/Buttons'
import { AutoCompleteField } from 'src/components/form/AutoCompleteField'
import { DateTimePickerField } from 'src/components/form/DateTimePickerField'
import { SwitchField } from 'src/components/form/SwitchField'
import { TextField } from 'src/components/form/TextField'
import { ScreenLayout } from 'src/components/screen-layout/ScreenLayout'
import { usePrompt } from 'src/routes/routing-hooks'
import { ConferenceService } from 'src/service/conference/ConferenceService'
import { CONFERENCE_SIZE, CONFERENCE_TYPE } from 'src/shared/constants/conference-constants'
import { CHANGES_SAVED, SOMETHING_WENT_WRONG, UNSAVED_CHANGES } from 'src/shared/constants/constants'
import { required } from 'src/shared/form/validators'
import { DateUtils } from 'src/shared/utils/date-utils'
import { getConferenceSize, getConferenceType, resolveSlug } from 'src/shared/utils/general-utils'

export const EditGeneralPage = () => {
  const navigate = useNavigate()
  const { conferenceId } = useParams()
  const conferenceService = useMemo(() => new ConferenceService(), [])
  const [initialValues, setInitialValues] = useState()
  const [loading, setLoading] = useState(false)
  const [formDirty, setFormDirty] = useState(false)

  usePrompt(UNSAVED_CHANGES, formDirty)

  useEffect(() => {
    const initData = async () => {
      if (conferenceId) {
        const { data, error } = await conferenceService.getConferenceById(conferenceId)
        if (error) {
          toast.error(SOMETHING_WENT_WRONG)
          return
        }
        data.start_date = DateUtils.formatDate(new Date(data.start_date))
        data.end_date = DateUtils.formatDate(new Date(data.end_date))
        setInitialValues(data)
      }
    }

    initData()
  }, [conferenceId, conferenceService])

  const onBack = () => {
    setTimeout(() => {
      navigate(`/conferences/details/${conferenceId}`)
    }, 200)
  }

  let submit = () => {}

  const onSave = async () => {
    await submit()
  }

  const onSubmitLocal = async (values) => {
    setFormDirty(false)
    setLoading(true)
    values.start_date = DateUtils.formatDateToISOString(values.start_date)
    values.end_date = DateUtils.formatDateToISOString(values.end_date)
    values = {
      ...values,
      slug: resolveSlug(values.title)
    }
    const { data, error } = await conferenceService.updateConference(conferenceId, values)
    if (data) {
      setFormDirty(false)
      setInitialValues(values)
      toast.success(CHANGES_SAVED)
      onBack()
    } else {
      toast.error(error.message)
      setLoading(false)
    }
  }

  return (
    <ScreenLayout title='Edit general information' onBack={onBack}>
      {initialValues && (
        <Form
          initialValues={initialValues}
          onSubmit={onSubmitLocal}
          render={({ handleSubmit, dirty }) => {
            submit = handleSubmit
            setFormDirty(dirty)
            return (
              <form onSubmit={handleSubmit} noValidate>
                <Grid container spacing={2}>
                  <Grid item xs={12} md={6}>
                    <TextField name='title' label='Conference Name' validate={required()} />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <TextField name='website' label='Website' validate={required()} />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <TextField name='description' label='Description' />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <TextField name='headquarter' label='Headquarters' validate={required()} />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <DateTimePickerField name='start_date' label='Start date' validate={required()} />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <DateTimePickerField name='end_date' label='End date' validate={required()} />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <AutoCompleteField
                      label='Event type'
                      name='event_type'
                      options={Object.keys(CONFERENCE_TYPE).map((type) => ({
                        label: getConferenceType(type),
                        value: type
                      }))}
                      validate={required()}
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <AutoCompleteField
                      label='Event size'
                      name='event_size'
                      options={Object.keys(CONFERENCE_SIZE).map((size) => ({
                        label: getConferenceSize(size),
                        value: size
                      }))}
                      validate={required()}
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <TextField name='video_url' label='Video URL' />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <Box width='100%' justifyContent='space-between' alignItems='center' display='flex'>
                      <Typography>Active</Typography>
                      <SwitchField label='Active' name='is_active' />
                    </Box>
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <TextField name='contact_name' label='Contact Name' validate={required()} />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <TextField name='contact_email' label='Contact Email ' validate={required()} />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <Box width='100%' justifyContent='space-between' alignItems='center' display='flex'>
                      <Typography>Ticketing</Typography>
                      <SwitchField label='' name='enable_ticketing' />
                    </Box>
                  </Grid>
                  <Grid item xs={12} md={12}>
                    <Divider />
                  </Grid>
                  <Grid item xs={12} md={12}>
                    <Box display='flex' justifyContent='flex-end' alignItems='center'>
                      <DiscardButton label='Cancel' sx={{ marginRight: 2 }} onClick={onBack} disabled={loading} />
                      <SaveButton label='Save' onClick={onSave} disabled={loading} />
                    </Box>
                  </Grid>
                </Grid>
              </form>
            )
          }}
        />
      )}
    </ScreenLayout>
  )
}
